import { useState } from "react";
import { toast } from "sonner";
import { PlayCircle, Clock, Plus, X } from "lucide-react";
import { endpoints, apiError } from "@/lib/api";
import { HighlightForm } from "@/pages/admin/AdminFbHighlights";

const chip = (on) => `shrink-0 h-10 px-3.5 rounded-full text-sm font-semibold border whitespace-nowrap inline-flex items-center gap-2 transition-colors ${on ? "bg-white text-black border-white" : "bg-[#121620] border-[#273042] text-slate-300 hover:text-white"}`;
const slug = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-");

export const CountryBar = ({ countries, value, onPick }) => (
  <div className="flex gap-2 overflow-x-auto pb-2 -mx-3 px-3 sm:mx-0 sm:px-0 sm:flex-wrap scrollbar-none" data-testid="hl-countries">
    {countries.map((c) => (
      <button key={c.name} onClick={() => onPick(c)} data-testid={`hl-country-${slug(c.name)}`} aria-pressed={value === c.name} className={chip(value === c.name)}>
        {c.flag && <img src={c.flag} alt="" className="w-5 h-5 rounded-full object-cover bg-white/10" loading="lazy" />}{c.name}
      </button>))}
  </div>
);

export const CompBar = ({ comps, value, onPick }) => comps.length < 2 ? null : (
  <div className="flex gap-2 overflow-x-auto pb-2 -mx-3 px-3 sm:mx-0 sm:px-0 sm:flex-wrap" data-testid="hl-competitions">
    {comps.map((c) => (
      <button key={c.id} onClick={() => onPick(c)} data-testid={`hl-comp-${c.id}`} aria-pressed={value === c.id}
        className={`shrink-0 h-8 px-3 rounded-full text-xs font-semibold border whitespace-nowrap transition-colors ${value === c.id ? "bg-[#E11D2A] border-[#E11D2A] text-white" : "border-[#273042] text-slate-400 hover:text-white"}`}>{c.name}</button>))}
  </div>
);

const Team = ({ t, right }) => (
  <span className={`flex items-center gap-2 min-w-0 flex-1 ${right ? "flex-row-reverse text-right" : ""}`}>
    {t.logoUrl ? <img src={t.logoUrl} alt="" loading="lazy" className="w-6 h-6 sm:w-7 sm:h-7 object-contain shrink-0" /> : <span className="w-6 h-6 rounded-full bg-white/10 shrink-0" />}
    <span className="text-white text-[13px] sm:text-sm font-semibold leading-tight line-clamp-2 hyphens-auto">{t.name}</span>
  </span>
);

export const ResultRow = ({ m, onWatch, onAdd }) => (
  <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 px-3 sm:px-4 py-3" data-testid={`hl-match-${m.matchId}`}>
    <div className="flex items-center gap-2 flex-1 min-w-0">
      <Team t={m.home} />
      <span className="shrink-0 min-w-[52px] sm:min-w-[64px] text-center rounded-lg bg-[#0A0C10] border border-[#273042] px-2 py-1 text-white font-extrabold tabular-nums" data-testid={`hl-score-${m.matchId}`}>
        {m.homeScore} - {m.awayScore}
      </span>
      <Team t={m.away} right />
    </div>
    <div className="flex items-center gap-2 sm:w-[260px] justify-end shrink-0 whitespace-nowrap">
      <span className="text-[11px] text-slate-500 mr-auto sm:mr-0">{(m.date || "").slice(11, 16)} · {m.statusLabel}</span>
      {m.video ? (
        <button onClick={() => onWatch(m)} data-testid={`hl-watch-${m.matchId}`}
          className="h-9 px-3.5 rounded-full bg-[#E11D2A] hover:bg-[#C8121F] text-white text-xs font-bold inline-flex items-center gap-1.5 transition-colors">
          <PlayCircle className="w-4 h-4" /> Watch highlights</button>
      ) : (
        <span className="h-9 px-3 rounded-full border border-[#273042] text-slate-500 text-xs font-semibold inline-flex items-center gap-1.5" data-testid={`hl-soon-${m.matchId}`}>
          <Clock className="w-3.5 h-3.5" /> Video soon</span>
      )}
      {onAdd && !m.video && (
        <button onClick={() => onAdd(m)} data-testid={`hl-add-video-${m.matchId}`} aria-label="Add highlight video"
          className="h-9 w-9 rounded-full border border-[#273042] text-slate-300 hover:text-white hover:border-[#E11D2A] inline-flex items-center justify-center"><Plus className="w-4 h-4" /></button>
      )}
    </div>
  </div>
);

export const AddVideoDialog = ({ m, competition, onClose, onSaved }) => {
  const init = { title: `${m.home.name} ${m.homeScore}-${m.awayScore} ${m.away.name} — Highlights`, url: "", thumbnail: "", fixtureId: String(m.matchId),
    homeName: m.home.name, awayName: m.away.name, league: competition, matchDate: (m.date || "").slice(0, 10), hidden: false };
  const [busy, setBusy] = useState(false);
  const save = async (f) => {
    if (!f.url.trim()) return toast.error("Upload a video or paste a link first");
    setBusy(true);
    try { await endpoints.adminFbCreateHighlight(f); toast.success("Highlight added"); onSaved(); }
    catch (e) { toast.error(apiError(e?.response?.data?.detail) || "Could not save"); }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-[110] flex items-end sm:items-center justify-center p-0 sm:p-6" data-testid="hl-add-dialog">
      <div className="absolute inset-0 bg-black/80" onClick={() => !busy && onClose()} />
      <div className="relative w-full sm:max-w-2xl max-h-[90vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl bg-[#121620] border border-[#273042] p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="text-white font-bold text-sm">Add video · {m.home.name} v {m.away.name}</div>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-white/5 text-white inline-flex items-center justify-center" data-testid="hl-add-close"><X className="w-4 h-4" /></button>
        </div>
        <HighlightForm initial={init} onSave={save} onCancel={onClose} />
      </div>
    </div>
  );
};
